'use client';
import { useState, useMemo, Fragment } from 'react';
import { ImageList, useMediaQuery, useTheme } from '@mui/material';
import PortfolioItem from './PortfolioItem';
import FullScreenView from './FullScreenView';
import TitleItem from './TitleItem';
import useIsMobile from '../../../../utils/useIsMobile';
import { GalleryGridType } from '../type';
import { groupBy, uniq } from 'lodash';
import { FullGalleryProject } from './types';
import Optional from '../../../Optional';

type Props = {
  data: GalleryGridType;
  initIsMobile: boolean;
};

const GalleryGrid = ({ data, initIsMobile }: Props) => {
  const { useFolder, variant } = data;
  const isMobile = useIsMobile(initIsMobile);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const allImages = useMemo(() => {
    const groupIds = uniq(data.images.map(({ image }) => image.folderId));
    const groupedImages = groupBy(data.images, 'image.folderId');
    const withInfo = data.images.map((project) => {
      return {
        ...project,
        imagesInFolder:
          groupedImages[project.image.folderId || 'undefined']?.length || 0,
      };
    });
    const ordered = groupIds.flatMap((id) => {
      return groupedImages[id || 'undefined']?.map((project) => ({
        ...project,
        imagesInFolder:
          groupedImages[project.image.folderId || 'undefined']?.length || 0,
      }));
    });
    return (data.useFolder ? ordered : withInfo).filter(
      (e): e is FullGalleryProject => !!e,
    );
  }, [data]);

  const columns = isMobile || isTablet ? 2 : 4;

  const layoutItems = useMemo(() => {
    let colPosition = 0;
    let nextOffset = 0;

    return allImages.map((project, index) => {
      const differentFolderThenPrevious =
        project?.image.folderId !== allImages[index - 1]?.image.folderId;
      const isEndOfProject = useFolder
        ? project?.image.folderId !== allImages[index + 1]?.image.folderId
        : !!allImages[index + 1]?.withTitle;
      const isLarge = useFolder
        ? index === 0 || differentFolderThenPrevious
        : !!project.withTitle;
      const wide =
        isLarge ||
        (isMobile && isEndOfProject && !(project.imagesInFolder % 2));
      const isTitleLarge = isMobile || !(colPosition % 2);

      colPosition += wide ? 2 : 1;

      if (isLarge) colPosition += isTitleLarge ? 2 : 1;
      colPosition += nextOffset;
      nextOffset = isLarge ? 2 : 0;

      return {
        project,
        index,
        wide,
        tall: isLarge,
        withTitle: isLarge,
        isTitleLarge,
      };
    });
  }, [allImages, isMobile, useFolder]);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setFullScreenOpen(true);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex(
      (prev) => (prev - 1 + allImages.length) % allImages.length,
    );
  };

  return (
    <>
      <ImageList
        sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
        variant={variant}
        cols={columns}
        rowHeight={variant !== 'masonry' ? (isMobile ? 200 : 450) : 'auto'}
      >
        {layoutItems.map(
          ({ project, index, wide, tall, withTitle, isTitleLarge }) => (
            <Fragment key={`${project.id}-wrapper`}>
              <Optional condition={withTitle}>
                <TitleItem
                  variant={data.titleVariant}
                  isLarge={variant === 'quilted' && isTitleLarge}
                  title={project.title}
                  subtitle={project.subtitle}
                />
              </Optional>
              <PortfolioItem
                variant={variant}
                project={project}
                wide={wide}
                tall={tall}
                onImageClick={() => handleImageClick(index)}
                isMobile={isMobile}
                priority={isMobile ? index < 3 : index < 5}
              />
            </Fragment>
          ),
        )}
      </ImageList>

      <FullScreenView
        open={fullScreenOpen}
        onClose={() => setFullScreenOpen(false)}
        projects={allImages}
        selectedIndex={selectedImageIndex}
        onNext={handleNextImage}
        onPrev={handlePrevImage}
      />
    </>
  );
};

export default GalleryGrid;
